"use client";

import type React from "react";
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Input,
  VStack,
  HStack,
  Text,
  Image,
  useToast,
  InputGroup,
  InputRightElement,
  InputLeftAddon,
  IconButton,
  Progress,
  Flex,
  Tooltip,
  Icon,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Center,
  Spinner,
  Heading,
  Card,
  CardBody,
  SimpleGrid,
} from "@chakra-ui/react";
import { FiUpload, FiTrash2 } from "react-icons/fi";
import {
  ViewIcon,
  ViewOffIcon,
  CheckIcon,
  QuestionIcon,
  CloseIcon,
} from "@chakra-ui/icons";
import { supabase } from "../supabaseClient";
import { useAuth } from "../contexts/AuthContext";
import DnsRecordsDialog from "./DnsRecordsDialog";
import UsersTable from "./UsersTable";
import RetellWebhookDialog from "./RetellWebhookDialog";
import PriceNumberInput, { normalizeNumericValue } from "./PriceNumberInput";
import { API_BASE_URL } from "../config";

const NUMERIC_PROFILE_FIELDS = [
  "monthly_subscription_price_usd",
  "one_credit_price_usd",
  "one_phone_number_monthly_price_usd",
  "ai_agents_call_price_per_minute_usd",
] as const;

type NumericProfileField = (typeof NUMERIC_PROFILE_FIELDS)[number];

const isNumericProfileField = (
  field: keyof ProfileData,
): field is NumericProfileField =>
  NUMERIC_PROFILE_FIELDS.includes(field as NumericProfileField);

interface ProfileData {
  company_name: string;
  main_logo_url: string;
  tab_logo_url: string;
  main_app_url: string;
  retellai_api_key: string;
  stripe_secret_key: string;
  subscription_status: "inactive" | "active" | "past_due" | "canceled";
  monthly_subscription_price_usd: number;
  one_credit_price_usd: number;
  one_phone_number_monthly_price_usd: number;
  ai_agents_call_price_per_minute_usd: number;
  main_app_url_verification_status:
    | "not_verified"
    | "pending"
    | "verified"
    | "failed";
  supabase_url?: string;
  onboarding_completed?: boolean;
  setup_completed?: boolean;
  supabase_service_key?: string;
  dns_setup_completed?: boolean;
  retell_setup_completed?: boolean;
}

interface DnsRecord {
  name: string;
  type: string;
  value: string;
  notes?: string; // Added notes field
}

const INSTANCE_SETUP_COMPLETE_KEY = "onboardingInstanceSetupComplete";

const instanceSetupInProgressKey = (userId: string) =>
  `onboardingInstanceSetupInProgress_${userId}`;

const isInstanceProvisioning = (
  profile: Pick<ProfileData, "onboarding_completed" | "setup_completed">,
) => !!profile.onboarding_completed && !profile.setup_completed;

const stages = [
  {
    id: 1,
    title: "Branding & AI",
    subtitle: "Make it yours",
    description:
      "Set up your brand identity, application URL, and Retell AI connection for your white-labeled AI Voice Agent platform.",
  },
  {
    id: 2,
    title: "Billing & Payments",
    subtitle: "Enable subscriptions",
    description:
      "Configure Stripe payment processing and set pricing for your white-labeled AI Voice Agent platform.",
  },
];

const OnboardingJourney: React.FC = () => {
  const { user } = useAuth(); // Using AuthContext hook
  const toast = useToast();
  const navigate = useNavigate();
  const [initialLoading, setInitialLoading] = useState(true);

  // ✅ currentStage is now 1-based: 1, 2, 3
  const [currentStage, setCurrentStage] = useState<number>(1);

  const [loading, setLoading] = useState<boolean>(false);
  const [loadingStep, setLoadingStep] = useState<string>("");
  const [uploading, setUploading] = useState({
    mainLogo: false,
    tabLogo: false,
  });
  const [, setSavingField] = useState<string | null>(null);
  const [urlWarning, setUrlWarning] = useState<string | null>(null);
  const mainLogoInputRef = useRef<HTMLInputElement>(null);
  const tabLogoInputRef = useRef<HTMLInputElement>(null);

  const [verifyingCertificate, setVerifyingCertificate] = useState<
    string | null
  >(null);
  const [dnsDialogOpen, setDnsDialogOpen] = useState(false);
  const [dnsRecords, setDnsRecords] = useState<DnsRecord[]>([]);
  const [dnsDialogDomain, setDnsDialogDomain] = useState<string>("");

  const [profileData, setProfileData] = useState<ProfileData>({
    company_name: "",
    main_logo_url: "",
    tab_logo_url: "",
    main_app_url: "",
    retellai_api_key: "",
    stripe_secret_key: "",
    subscription_status: "inactive",
    monthly_subscription_price_usd: 0,
    one_credit_price_usd: 0,
    one_phone_number_monthly_price_usd: 0,
    ai_agents_call_price_per_minute_usd: 0,
    main_app_url_verification_status: "not_verified",
    onboarding_completed: false,
    setup_completed: false,
    supabase_url: undefined,
    supabase_service_key: undefined,
    dns_setup_completed: false,
    retell_setup_completed: false,
  });

  const [showKeys, setShowKeys] = useState({
    retellai_api_key: false,
    stripe_secret_key: false,
  });

  const [hasSubscription, setHasSubscription] = useState<boolean>(false);
  const [checkingSubscription, setCheckingSubscription] =
    useState<boolean>(true);

  const [showUsersTable, setShowUsersTable] = useState(false);
  const [instanceCredentials, setInstanceCredentials] = useState<{
    supabaseUrl: string;
    supabaseServiceKey: string;
  } | null>(null);

  const [deploymentInProgress, setDeploymentInProgress] = useState(false);
  const [deploymentStatus, setDeploymentStatus] = useState<string>("");
  const [awaitingInstanceSetup, setAwaitingInstanceSetup] = useState(false);
  const [showCnameDialog, setShowCnameDialog] = useState(false);
  const [cnameRecords, setCnameRecords] = useState<DnsRecord[]>([]);
  const [showRetellWebhookDialog, setShowRetellWebhookDialog] = useState(false);
  const [isConfirmingCname, setIsConfirmingCname] = useState(false);
  const [isCompletingRetellSetup, setIsCompletingRetellSetup] = useState(false);

  const triggerSetupCompleteReload = useCallback(() => {
    sessionStorage.setItem(INSTANCE_SETUP_COMPLETE_KEY, "1");
    window.location.reload();
  }, []);

  const applyPostSetupFromAlbStatus = useCallback(
    (
      albData: {
        bothReady: boolean;
        frontendDnsName?: string | null;
        frontendState?: string;
      },
      mainAppUrl: string,
      dnsSetupCompleted: boolean,
      retellSetupCompleted: boolean,
    ) => {
      if (!albData.bothReady) {
        setDeploymentInProgress(true);
        setDeploymentStatus(
          `Deployment in progress... (${albData.frontendState || "provisioning"})`,
        );
        return;
      }

      setDeploymentInProgress(false);

      if (!dnsSetupCompleted) {
        const mainAppUrlParts = mainAppUrl.split(".");
        setCnameRecords([
          {
            name: mainAppUrlParts[0],
            type: "CNAME",
            value: albData.frontendDnsName || "",
            notes: "Application",
          },
        ]);
        setShowCnameDialog(true);
      } else if (!retellSetupCompleted) {
        setShowRetellWebhookDialog(true);
      }
    },
    [],
  );

  useEffect(() => {
    if (!user?.id || !awaitingInstanceSetup) return;

    const pollForSetupCompletion = async () => {
      try {
        const { data, error } = await supabase
          .from("profiles")
          .select("setup_completed, supabase_url, supabase_service_key")
          .eq("id", user.id)
          .single();

        if (error) return;

        if (
          data?.setup_completed &&
          data?.supabase_url &&
          data?.supabase_service_key
        ) {
          if (user?.id) {
            sessionStorage.removeItem(instanceSetupInProgressKey(user.id));
          }
          setAwaitingInstanceSetup(false);
          triggerSetupCompleteReload();
        }
      } catch (err) {
        console.error("[Onboarding] Error polling setup status:", err);
      }
    };

    pollForSetupCompletion();
    const intervalId = setInterval(pollForSetupCompletion, 10000);

    return () => clearInterval(intervalId);
  }, [awaitingInstanceSetup, user?.id, triggerSetupCompleteReload]);

  useEffect(() => {
    if (
      !profileData.setup_completed ||
      !profileData.onboarding_completed ||
      !instanceCredentials ||
      !profileData.main_app_url
    ) {
      return;
    }

    if (profileData.dns_setup_completed && profileData.retell_setup_completed) {
      return;
    }

    const checkSetupStatusAndShowDialogs = async () => {
      try {
        if (
          profileData.dns_setup_completed &&
          !profileData.retell_setup_completed
        ) {
          setShowRetellWebhookDialog(true);
          return;
        }

        const checkAlbResponse = await fetch(
          `${API_BASE_URL}/api/aws-alb/check-alb-status`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              mainAppUrl: profileData.main_app_url,
            }),
          },
        );

        if (checkAlbResponse.ok) {
          const albData = await checkAlbResponse.json();
          applyPostSetupFromAlbStatus(
            albData,
            profileData.main_app_url,
            profileData.dns_setup_completed || false,
            profileData.retell_setup_completed || false,
          );
        } else {
          console.error("Error checking ALB status:", checkAlbResponse.status);
        }
      } catch (error) {
        console.error("Error in checkSetupStatusAndShowDialogs:", error);
      }
    };

    checkSetupStatusAndShowDialogs();
  }, [
    profileData.setup_completed,
    profileData.onboarding_completed,
    profileData.dns_setup_completed,
    profileData.retell_setup_completed,
    profileData.main_app_url,
    instanceCredentials,
    applyPostSetupFromAlbStatus,
  ]);

  useEffect(() => {
    if (!deploymentInProgress) return;

    const pollInterval = setInterval(async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/aws-alb/check-alb-status`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              mainAppUrl: profileData.main_app_url,
            }),
          },
        );

        if (response.ok) {
          const data = await response.json();

          if (data.bothReady) {
            setDeploymentStatus(
              "Deployment complete! Application ALB is active.",
            );
            clearInterval(pollInterval);

            if (profileData.main_app_url) {
              applyPostSetupFromAlbStatus(
                data,
                profileData.main_app_url,
                profileData.dns_setup_completed || false,
                profileData.retell_setup_completed || false,
              );
            }
          } else {
            setDeploymentStatus(
              `Checking deployment status... Application: ${data.frontendState}`,
            );
          }
        } else {
          setDeploymentStatus("Checking for AWS Load Balancers...");
        }
      } catch (error) {
        console.error("Error polling ALB status:", error);
        setDeploymentStatus(
          "Error checking AWS Load Balancers. Please try again later.",
        );
        // Optionally clear interval on error if it's persistent
        // clearInterval(pollInterval);
      }
    }, 15000); // Poll every 15 seconds

    return () => clearInterval(pollInterval);
  }, [
    deploymentInProgress,
    profileData.main_app_url,
    profileData.dns_setup_completed,
    profileData.retell_setup_completed,
    applyPostSetupFromAlbStatus,
  ]);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user?.id) {
        setInitialLoading(false);
        return;
      }

      console.log("[v0] Starting profile fetch for user:", user.id);
      setInitialLoading(true);

      try {
        const { data, error } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", user.id)
          .single();

        console.log("[v0] Profile data received:", data);

        if (error) throw error;

        if (data) {
          setProfileData({
            company_name: data.company_name || "",
            retellai_api_key: data.retellai_api_key || "",
            stripe_secret_key: data.stripe_secret_key || "",
            main_app_url: data.main_app_url || "",
            main_logo_url: data.main_logo_url || "",
            tab_logo_url: data.tab_logo_url || "",
            subscription_status: data.subscription_status || "inactive",
            monthly_subscription_price_usd: normalizeNumericValue(
              data.monthly_subscription_price_usd,
              true,
            ),
            one_credit_price_usd: normalizeNumericValue(
              data.one_credit_price_usd,
            ),
            one_phone_number_monthly_price_usd: normalizeNumericValue(
              data.one_phone_number_monthly_price_usd,
            ),
            ai_agents_call_price_per_minute_usd: normalizeNumericValue(
              data.ai_agents_call_price_per_minute_usd,
            ),
            main_app_url_verification_status:
              data.main_app_url_verification_status || "not_verified",
            supabase_url: data.supabase_url || undefined,
            onboarding_completed: data.onboarding_completed || false,
            setup_completed: data.setup_completed || false,
            supabase_service_key: data.supabase_service_key || undefined,
            dns_setup_completed: data.dns_setup_completed || false,
            retell_setup_completed: data.retell_setup_completed || false,
          });

          console.log(
            "[v0] Profile state updated with company_name:",
            data.company_name,
          );

          if (data.setup_completed && user?.id) {
            sessionStorage.removeItem(instanceSetupInProgressKey(user.id));
          }

          if (
            data.setup_completed &&
            data.supabase_url &&
            data.supabase_service_key
          ) {
            setInstanceCredentials({
              supabaseUrl: data.supabase_url,
              supabaseServiceKey: data.supabase_service_key,
            });
            setShowUsersTable(true);
          }

          const hasPersistedProvisioning =
            data.onboarding_completed && !data.setup_completed;
          const hasSessionProvisioning =
            user?.id &&
            sessionStorage.getItem(instanceSetupInProgressKey(user.id)) ===
              "1" &&
            !data.setup_completed;

          if (hasPersistedProvisioning || hasSessionProvisioning) {
            setAwaitingInstanceSetup(true);
            setDeploymentInProgress(true);
            setDeploymentStatus(
              "Your instance is being provisioned... This may take 10-15 minutes. We'll have it ready for you soon.",
            );
            if (hasPersistedProvisioning && user?.id) {
              sessionStorage.removeItem(instanceSetupInProgressKey(user.id));
            }
          }

          if (sessionStorage.getItem(INSTANCE_SETUP_COMPLETE_KEY)) {
            sessionStorage.removeItem(INSTANCE_SETUP_COMPLETE_KEY);
            toast({
              title: "Setup complete!",
              description:
                "Your instance has been created successfully. Supabase, Stripe, and your platform are ready.",
              status: "success",
              duration: 8000,
              isClosable: true,
              position: "top",
            });
          }
          // Set deployment state if already completed but user is on this page
          if (data.setup_completed && data.main_app_url) {
            const checkAlbResponse = await fetch(
              `${API_BASE_URL}/api/aws-alb/check-alb-status`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  mainAppUrl: data.main_app_url,
                }),
              },
            );
            if (checkAlbResponse.ok) {
              const albData = await checkAlbResponse.json();
              applyPostSetupFromAlbStatus(
                albData,
                data.main_app_url,
                data.dns_setup_completed || false,
                data.retell_setup_completed || false,
              );
            } else {
              console.error(
                "Error checking ALB status during profile fetch:",
                checkAlbResponse.status,
              );
              // If check fails, assume deployment is in progress or needs to be checked
              setDeploymentInProgress(true);
              setDeploymentStatus("Checking deployment status...");
            }
          }
        }
      } catch (error: any) {
        console.error("[v0] Error fetching profile:", error);
        toast({
          title: "Error fetching profile",
          description: error.message || "Could not load your settings.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setInitialLoading(false);
        console.log("[v0] Initial loading complete");
      }
    };

    if (user) {
      fetchProfile();
    } else {
      setInitialLoading(false); // Ensure loading stops if no user
    }
  }, [user, toast, applyPostSetupFromAlbStatus]);

  useEffect(() => {
    const fetchSubscription = async () => {
      if (!user?.id) return;

      try {
        const response = await fetch(
          `${API_BASE_URL}/api/billing/subscription`,
          {
            headers: {
              "user-id": user.id,
            },
          },
        );

        if (response.ok) {
          const data = await response.json();
          const isActive =
            data.subscription?.status === "active" ||
            data.subscription?.status === "trialing";
          setHasSubscription(isActive);
        } else if (response.status === 404) {
          // No subscription found, treat as inactive
          setHasSubscription(false);
        } else {
          console.error(
            "Error fetching subscription:",
            response.status,
            await response.text(),
          );
          toast({
            title: "Error fetching subscription",
            description: "Could not retrieve subscription status.",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
        }
      } catch (error) {
        console.error("Error fetching subscription:", error);
        // Connection refused means the dashboard API is not running yet.
        setHasSubscription(false);
      } finally {
        setCheckingSubscription(false);
      }
    };

    fetchSubscription();
  }, [user, toast]);

  useEffect(() => {
    const checkPendingCertificates = async () => {
      if (!user?.id || initialLoading) return;

      try {
        const response = await fetch(
          `${API_BASE_URL}/api/certificates/check-pending-certificates`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ userId: user.id }),
          },
        );

        if (response.ok) {
          const data = await response.json();
          setProfileData((prev) => ({
            ...prev,
            main_app_url_verification_status:
              data.main_app_url_status || prev.main_app_url_verification_status,
          }));
        } else {
          console.error(
            "Error checking pending certificates:",
            response.status,
          );
        }
      } catch (error) {
        console.error("Error checking pending certificates:", error);
      }
    };

    checkPendingCertificates();
  }, [user, initialLoading]);

  const handleInputChange = (field: keyof ProfileData, value: string) => {
    setProfileData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleNumberChange = (
    field: NumericProfileField,
    valueAsString: string,
    valueAsNumber: number,
    isInteger = false,
  ) => {
    const value = normalizeNumericValue(
      valueAsString === "" ? 0 : valueAsNumber,
      isInteger,
    );
    setProfileData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const validateSubdomain = (url: string): boolean => {
    if (!url) return false;
    // Allow simple domains like example.com or sub.example.com
    const parts = url.split(".");
    return parts.length >= 2;
  };

  const handleUrlChange = (field: keyof ProfileData, value: string) => {
    let cleanedValue = value;

    if (
      cleanedValue.startsWith("https://") ||
      cleanedValue.startsWith("http://")
    ) {
      cleanedValue = cleanedValue.replace(/^https?:\/\//, "");
      setUrlWarning(`Removed protocol from URL. Use format: ${cleanedValue}`);
      setTimeout(() => setUrlWarning(null), 3000);
    }

    cleanedValue = cleanedValue.replace(/\/$/, "");

    setProfileData((prev) => ({
      ...prev,
      [field]: cleanedValue,
    }));
  };

  const verifyCertificate = async (field: "main_app_url", url: string) => {
    if (!user?.id || !url) return;

    setVerifyingCertificate(field);

    try {
      const checkResponse = await fetch(
        `${API_BASE_URL}/api/certificates/check-certificate`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            url,
            userId: user.id,
            fieldName: field,
          }),
        },
      );

      if (checkResponse.status === 409) {
        const errorData = await checkResponse.json();
        toast({
          title: "Domain already in use",
          description:
            errorData.message ||
            "This domain's certificate is already in use. Please use a different subdomain.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        setVerifyingCertificate(null);
        return;
      }

      if (checkResponse.ok) {
        const checkData = await checkResponse.json();

        if (checkData.status === "verified") {
          setProfileData((prev) => ({
            ...prev,
            [`${field}_verification_status`]: "verified",
          }));
          toast({
            title: "Certificate verified",
            description: "SSL certificate is already issued for this domain.",
            status: "success",
            duration: 3000,
            isClosable: true,
          });
          setVerifyingCertificate(null);
          return;
        }

        if (
          checkData.status === "pending" &&
          checkData.dnsRecords &&
          checkData.dnsRecords.length > 0
        ) {
          setProfileData((prev) => ({
            ...prev,
            [`${field}_verification_status`]: "pending",
          }));
          setDnsRecords(checkData.dnsRecords);
          setDnsDialogDomain(url);
          setDnsDialogOpen(true);
          setVerifyingCertificate(null);
          return;
        }

        const requestResponse = await fetch(
          `${API_BASE_URL}/api/certificates/request-certificate`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              url,
              userId: user.id,
              fieldName: field,
            }),
          },
        );

        if (requestResponse.ok) {
          const requestData = await requestResponse.json();

          if (requestData.dnsRecords && requestData.dnsRecords.length > 0) {
            setProfileData((prev) => ({
              ...prev,
              [`${field}_verification_status`]: "pending",
            }));
            setDnsRecords(requestData.dnsRecords);
            setDnsDialogDomain(url);
            setDnsDialogOpen(true);

            toast({
              title: "Certificate requested",
              description:
                "Please add the DNS records shown to verify your domain.",
              status: "info",
              duration: 5000,
              isClosable: true,
            });
          } else {
            toast({
              title: "Error",
              description: "Failed to retrieve DNS records. Please try again.",
              status: "error",
              duration: 3000,
              isClosable: true,
            });
          }
        } else {
          const reqErrorData = await requestResponse.json();
          toast({
            title: "Request failed",
            description:
              reqErrorData.error ||
              "Failed to request certificate. Please try again.",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
        }
      } else {
        const checkErrorData = await checkResponse.json();
        toast({
          title: "Check failed",
          description:
            checkErrorData.error ||
            "Failed to check certificate status. Please try again.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error verifying certificate:", error);
      toast({
        title: "Verification error",
        description: "Failed to verify certificate. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setVerifyingCertificate(null);
    }
  };

  const handleFieldBlur = async (field: keyof ProfileData) => {
    if (!user?.id) return;

    if (field === "main_app_url") {
      const url = profileData[field];
      if (url && !validateSubdomain(url)) {
        toast({
          title: "Invalid URL format",
          description:
            "Please enter a URL with a subdomain (e.g., subdomain.domain.com)",
          status: "error",
          duration: 4000,
          isClosable: true,
          position: "bottom-right",
        });
        return;
      }
    }

    setSavingField(field);

    const isInteger = field === "monthly_subscription_price_usd";
    const valueToSave = isNumericProfileField(field)
      ? normalizeNumericValue(profileData[field], isInteger)
      : profileData[field];

    if (isNumericProfileField(field) && valueToSave !== profileData[field]) {
      setProfileData((prev) => ({ ...prev, [field]: valueToSave }));
    }

    try {
      const { error } = await supabase
        .from("profiles")
        .update({
          [field]: valueToSave,
          updated_at: new Date().toISOString(),
        })
        .eq("id", user.id);

      if (error) throw error;

      console.log(`[v0] Auto-saved ${field}`);

      const fieldName = field
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (str) => str.toUpperCase())
        .trim();

      toast({
        title: `${fieldName} saved`,
        description: "Your changes have been saved automatically.",
        status: "success",
        duration: 2000,
        isClosable: true,
        position: "bottom-right",
      });

      if (field === "main_app_url") {
        await verifyCertificate(field, profileData[field]);
      }
    } catch (error: any) {
      console.error(`Error auto-saving ${field}:`, error);
      toast({
        title: "Error saving",
        description: error.message || "Failed to save field. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "bottom-right",
      });
    } finally {
      setSavingField(null);
    }
  };

  const toggleShowKey = (field: keyof typeof showKeys) => {
    setShowKeys((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleLogoUpload = async (file: File, logoType: "main" | "tab") => {
    if (!user?.id) return;

    const uploadingKey = logoType === "main" ? "mainLogo" : "tabLogo";
    setUploading((prev) => ({ ...prev, [uploadingKey]: true }));

    try {
      const fileExt = file.name.split(".").pop();
      if (!fileExt) {
        throw new Error("Invalid file format");
      }
      const fileName = `${user.id}/${logoType}-logo-${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from("logos")
        .upload(fileName, file, {
          cacheControl: "3600",
          upsert: true,
        });

      if (uploadError) throw uploadError;

      const {
        data: { publicUrl },
      } = supabase.storage.from("logos").getPublicUrl(fileName);

      const urlField = logoType === "main" ? "main_logo_url" : "tab_logo_url";

      const { error: dbError } = await supabase
        .from("profiles")
        .update({
          [urlField]: publicUrl,
          updated_at: new Date().toISOString(),
        })
        .eq("id", user.id);

      if (dbError) throw dbError;

      setProfileData((prev) => ({
        ...prev,
        [urlField]: publicUrl,
      }));

      toast({
        title: "Logo uploaded",
        description: `${
          logoType === "main" ? "Main" : "Tab"
        } logo uploaded and saved successfully.`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error: any) {
      console.error("Error uploading logo:", error);
      toast({
        title: "Error",
        description:
          error.message || "Failed to upload logo. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setUploading((prev) => ({ ...prev, [uploadingKey]: false }));
    }
  };

  const handleLogoDelete = async (logoType: "main" | "tab") => {
    if (!user?.id) return;

    const urlField = logoType === "main" ? "main_logo_url" : "tab_logo_url";
    const currentUrl = profileData[urlField];

    if (!currentUrl) return;

    try {
      // Extract file path from URL
      const urlObject = new URL(currentUrl);
      const path = urlObject.pathname.split("/").slice(2).join("/"); // e.g., 'logos/user_id/logo-name.png'

      await supabase.storage.from("logos").remove([path]);

      const { error } = await supabase
        .from("profiles")
        .update({
          [urlField]: "",
          updated_at: new Date().toISOString(),
        })
        .eq("id", user.id);

      if (error) throw error;

      setProfileData((prev) => ({
        ...prev,
        [urlField]: "",
      }));

      toast({
        title: "Logo deleted",
        description: `${
          logoType === "main" ? "Main" : "Tab"
        } logo removed successfully.`,
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (error: any) {
      console.error("Error deleting logo:", error);
      toast({
        title: "Error",
        description:
          error.message || "Failed to delete logo. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const isStageComplete = useCallback(
    (stage: number): boolean => {
      switch (stage) {
        case 1: {
          return !!(
            profileData.main_logo_url &&
            profileData.tab_logo_url &&
            profileData.main_app_url &&
            profileData.main_app_url_verification_status === "verified" &&
            profileData.retellai_api_key
          );
        }
        case 2: {
          return !!profileData.stripe_secret_key;
        }
        default:
          return false;
      }
    },
    [profileData],
  );

  const stage1Complete = useMemo(() => isStageComplete(1), [isStageComplete]);
  const stage2Complete = useMemo(() => isStageComplete(2), [isStageComplete]);
  const allStagesComplete = useMemo(
    () => stage1Complete && stage2Complete,
    [stage1Complete, stage2Complete],
  );

  const completedStageIds = useMemo(
    () => stages.filter((stage) => isStageComplete(stage.id)).map((s) => s.id),
    [isStageComplete],
  );

  const progressPercentage = useMemo(() => {
    if (
      profileData.setup_completed ||
      deploymentInProgress ||
      isInstanceProvisioning(profileData) ||
      (loading && currentStage === stages.length)
    ) {
      return 100;
    }

    const stage1Requirements = [
      !!profileData.main_logo_url,
      !!profileData.tab_logo_url,
      !!profileData.main_app_url,
      profileData.main_app_url_verification_status === "verified",
      !!profileData.retellai_api_key,
    ];
    const stage2Requirements = [!!profileData.stripe_secret_key];

    const stage1Progress =
      stage1Requirements.filter(Boolean).length / stage1Requirements.length;
    const stage2Progress =
      stage2Requirements.filter(Boolean).length / stage2Requirements.length;

    return ((stage1Progress + stage2Progress) / stages.length) * 100;
  }, [profileData, deploymentInProgress, loading, currentStage]);

  const handleStageClick = (stageId: number) => {
    setCurrentStage(stageId);
  };

  const handleCnameConfirmed = async () => {
    if (!user?.id || !instanceCredentials) return;

    setIsConfirmingCname(true);
    try {
      const { error: updateError } = await supabase
        .from("profiles")
        .update({ dns_setup_completed: true })
        .eq("id", user.id);

      if (updateError) {
        throw updateError;
      }

      setProfileData((prev) => ({
        ...prev,
        dns_setup_completed: true,
      }));

      setShowCnameDialog(false);

      if (!profileData.retell_setup_completed) {
        setShowRetellWebhookDialog(true);
      } else {
        setShowUsersTable(true);
      }
    } catch (error: any) {
      console.error("Error updating DNS setup:", error);
      toast({
        title: "Error",
        description:
          error.message || "Failed to update DNS setup. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsConfirmingCname(false);
    }
  };

  const handleRetellWebhookComplete = async () => {
    if (!user?.id) return;

    setIsCompletingRetellSetup(true);
    try {
      const { error: updateError } = await supabase
        .from("profiles")
        .update({ retell_setup_completed: true })
        .eq("id", user.id);

      if (updateError) {
        throw updateError;
      }

      setProfileData((prev) => ({
        ...prev,
        retell_setup_completed: true,
      }));

      setShowRetellWebhookDialog(false);

      if (instanceCredentials) {
        setShowUsersTable(true);
      }
    } catch (error: any) {
      console.error("Error updating Retell setup:", error);
      toast({
        title: "Error",
        description:
          error.message || "Failed to update Retell setup. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsCompletingRetellSetup(false);
    }
  };

  const handleSaveAndContinue = async () => {
    if (!user?.id) {
      toast({
        title: "Authentication required",
        description: "Please log in to continue",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    setLoadingStep("Saving your configuration...");

    try {
      const { error } = await supabase
        .from("profiles")
        .update({
          company_name: profileData.company_name,
          retellai_api_key: profileData.retellai_api_key,
          stripe_secret_key: profileData.stripe_secret_key,
          main_app_url: profileData.main_app_url,
          monthly_subscription_price_usd: normalizeNumericValue(
            profileData.monthly_subscription_price_usd,
            true,
          ),
          one_credit_price_usd: normalizeNumericValue(
            profileData.one_credit_price_usd,
          ),
          one_phone_number_monthly_price_usd: normalizeNumericValue(
            profileData.one_phone_number_monthly_price_usd,
          ),
          ai_agents_call_price_per_minute_usd: normalizeNumericValue(
            profileData.ai_agents_call_price_per_minute_usd,
          ),
          main_logo_url: profileData.main_logo_url,
          tab_logo_url: profileData.tab_logo_url,
          updated_at: new Date().toISOString(),
        })
        .eq("id", user.id);

      if (error) throw error;

      if (currentStage < stages.length) {
        // ✅ Always allow going to the next stage
        setCurrentStage(currentStage + 1);
        // Clear loading step when moving to next stage
        setLoadingStep("");
      } else {
        if (
          deploymentInProgress ||
          profileData.setup_completed ||
          isInstanceProvisioning(profileData)
        ) {
          return;
        }

        if (user?.id) {
          sessionStorage.setItem(instanceSetupInProgressKey(user.id), "1");
        }

        setAwaitingInstanceSetup(true);
        setDeploymentInProgress(true);
        setLoadingStep("Initializing your Database...");
        toast({
          title: "Setting up your instance...",
          description: "This may take several minutes. Please wait.",
          status: "info",
          duration: 8000,
          isClosable: true,
        });

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 300000); // 5 minute timeout

        let createInstanceResponse;
        try {
          createInstanceResponse = await fetch(
            `${API_BASE_URL}/api/supabase-provisioning/create-instance`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                userId: user.id,
                mainAppUrl: profileData.main_app_url,
              }),
              signal: controller.signal,
            },
          );
        } catch (fetchError: any) {
          clearTimeout(timeoutId);

          if (fetchError.name === "AbortError") {
            toast({
              title: "Instance Setup in Progress",
              description:
                "Your instance is being provisioned in the background. This page will refresh automatically when ready.",
              status: "info",
              duration: 10000,
              isClosable: true,
            });
            setAwaitingInstanceSetup(true);
            setDeploymentInProgress(true);
            setDeploymentStatus(
              "Your instance is being provisioned... This may take 10-15 minutes. We'll have it ready for you soon.",
            );
            return; // Exit gracefully without throwing error
          }
          throw fetchError;
        }

        clearTimeout(timeoutId);

        const contentType = createInstanceResponse.headers.get("content-type");

        if (!createInstanceResponse.ok) {
          if (createInstanceResponse.status === 409) {
            setProfileData((prev) => ({
              ...prev,
              onboarding_completed: true,
            }));
            toast({
              title: "Instance Setup in Progress",
              description:
                "Your instance is already being provisioned. This page will refresh automatically when ready.",
              status: "info",
              duration: 10000,
              isClosable: true,
            });
            setAwaitingInstanceSetup(true);
            setDeploymentInProgress(true);
            setDeploymentStatus(
              "Your instance is being provisioned... This may take 10-15 minutes. We'll have it ready for you soon.",
            );
            return;
          }

          if (createInstanceResponse.status === 504) {
            toast({
              title: "Instance Setup in Progress",
              description:
                "Your instance is being provisioned in the background. This page will refresh automatically when ready.",
              status: "info",
              duration: 10000,
              isClosable: true,
            });
            setAwaitingInstanceSetup(true);
            setDeploymentInProgress(true);
            setDeploymentStatus(
              "Your instance is being provisioned... This may take 10-15 minutes. Check back shortly for updates.",
            );
            return; // Exit gracefully without throwing error
          }

          let errorMessage = "Failed to create Supabase instance";

          // Only try to parse JSON if the content type is actually JSON
          if (contentType && contentType.includes("application/json")) {
            try {
              const errorData = await createInstanceResponse.json();
              errorMessage = errorData.error || errorMessage;
            } catch (parseError) {
              console.error(
                "[v0] Failed to parse error response as JSON:",
                parseError,
              );
            }
          } else {
            // It's HTML or something else, likely an error page
            const textResponse = await createInstanceResponse.text();
            console.error(
              "[v0] Received non-JSON response:",
              textResponse.substring(0, 200),
            );
            errorMessage = `Server returned an error (${createInstanceResponse.status}). The instance creation may still be in progress.`;
          }

          throw new Error(errorMessage);
        }

        let data;
        try {
          if (contentType && contentType.includes("application/json")) {
            data = await createInstanceResponse.json();
          } else {
            throw new Error(
              "Server returned non-JSON response. Instance creation may still be in progress.",
            );
          }
        } catch (parseError) {
          console.error(
            "[v0] Failed to parse success response as JSON:",
            parseError,
          );
          throw new Error(
            "Instance creation may have succeeded but we couldn't parse the response. Please refresh the page to check your instance status.",
          );
        }

        console.log("Instance creation response:", data);

        setLoadingStep("Finalizing your instance...");
        setAwaitingInstanceSetup(false);
        triggerSetupCompleteReload();
        return;
      }
    } catch (error: any) {
      console.error("[v0] Error in handleSaveAndContinue:", error);
      toast({
        title: "Error",
        description: error.message || "An unexpected error occurred",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
      setLoadingStep("");
    }
  };

  if (initialLoading || checkingSubscription) {
    return (
      <Container maxW="7xl" py={8}>
        <Center minH="60vh">
          <VStack spacing={4}>
            <Spinner size="xl" color="blue.500" thickness="4px" />
            <Text fontSize="lg" color="gray.600">
              Loading your onboarding journey...
            </Text>
          </VStack>
        </Center>
      </Container>
    );
  }

  // ✅ Safely map currentStage (1–3) to the array index (0–2)
  const currentStageData = stages[currentStage - 1] ?? stages[0];
  const bgColor = "gray.50"; // Define bgColor for consistent use

  return (
    <Box w="full" minH="100vh" bg={bgColor} py={8}>
      <Container maxW="6xl">
        {loading && loadingStep && (
          <Box
            position="fixed"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg="blackAlpha.700"
            zIndex={9999}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <VStack
              spacing={4}
              bg={bgColor}
              p={8}
              borderRadius="lg"
              boxShadow="xl"
            >
              <Spinner size="xl" color="blue.500" thickness="4px" />
              <Text fontSize="lg" fontWeight="semibold">
                {loadingStep}
              </Text>
              <Text fontSize="sm" color="gray.500">
                Please do not close this window
              </Text>
            </VStack>
          </Box>
        )}

        {showUsersTable ? (
          <Box>
            {deploymentInProgress && (
              <Box bg="blue.50" borderBottom="1px" borderColor="blue.200" p={4}>
                <VStack spacing={2}>
                  <HStack>
                    <Spinner size="sm" color="blue.500" />
                    <Text fontWeight="bold" color="blue.700">
                      {deploymentStatus}
                    </Text>
                  </HStack>
                  <Text fontSize="sm" color="blue.600">
                    Checking for AWS Load Balancers... This process may take
                    10-15 minutes.
                  </Text>
                </VStack>
              </Box>
            )}

            {instanceCredentials && (
              <UsersTable
                supabaseUrl={instanceCredentials.supabaseUrl}
                supabaseServiceKey={instanceCredentials.supabaseServiceKey}
                onboardingData={profileData}
              />
            )}
          </Box>
        ) : (
          <>
            {/* Header */}
            <Box bg="white" borderBottom="1px" borderColor="gray.200" py={6}>
              <Container maxW="container.lg">
                <VStack spacing={4} align="stretch">
                  <Flex justify="space-between" align="center">
                    <Box>
                      <Text fontSize="sm" color="gray.600" mb={1}>
                        Stage {currentStage} of {stages.length}
                      </Text>
                      <Heading size="lg">{currentStageData.title}</Heading>
                      <Text color="gray.600" fontSize="sm" mt={1}>
                        {currentStageData.subtitle}
                      </Text>
                    </Box>
                    <Text
                      fontSize="md"
                      px={4}
                      py={2}
                      borderRadius="full"
                      borderWidth="1px"
                      borderColor="blue.100"
                      bg="blue.50"
                      color="blue.700"
                      fontWeight="medium"
                    >
                      {Math.round(progressPercentage)}% Complete
                    </Text>
                  </Flex>
                  <Progress
                    value={progressPercentage}
                    size="sm"
                    colorScheme="blue"
                    borderRadius="full"
                  />

                  {/* Stage Indicators */}
                  <HStack spacing={4} justify="center" pt={2}>
                    {stages.map((stage) => (
                      <HStack key={stage.id} spacing={2}>
                        <Flex
                          w="40px"
                          h="40px"
                          borderRadius="full"
                          bg={
                            completedStageIds.includes(stage.id)
                              ? "green.500"
                              : stage.id === currentStage
                                ? "blue.500"
                                : "gray.200"
                          }
                          align="center"
                          justify="center"
                          color="white"
                          fontWeight="bold"
                          transition="all 0.3s"
                          _hover={{
                            transform: "scale(1.1)",
                            shadow: "md",
                          }}
                          cursor="pointer"
                          onClick={() => handleStageClick(stage.id)}
                        >
                          {completedStageIds.includes(stage.id) ? (
                            <Icon as={CheckIcon} />
                          ) : (
                            stage.id
                          )}
                        </Flex>
                        <Box display={{ base: "none", md: "block" }}>
                          <Text
                            fontSize="xs"
                            fontWeight={
                              stage.id === currentStage ? "bold" : "normal"
                            }
                            color={
                              stage.id === currentStage
                                ? "blue.600"
                                : "gray.600"
                            }
                          >
                            {stage.title}
                          </Text>
                        </Box>
                      </HStack>
                    ))}
                  </HStack>

                  {urlWarning && (
                    <Alert status="info" borderRadius="md">
                      <AlertIcon />
                      <AlertDescription fontSize="sm">
                        {urlWarning}
                      </AlertDescription>
                      <IconButton
                        aria-label="Close warning"
                        icon={<CloseIcon />}
                        size="xs"
                        variant="ghost"
                        ml="auto"
                        onClick={() => setUrlWarning(null)}
                      />
                    </Alert>
                  )}

                  {(awaitingInstanceSetup ||
                    isInstanceProvisioning(profileData)) &&
                    !profileData.setup_completed && (
                      <Alert status="info" borderRadius="md">
                        <AlertIcon />
                        <Box flex="1">
                          <AlertTitle fontSize="sm">
                            Setting up your instance
                          </AlertTitle>
                          <AlertDescription fontSize="sm">
                            Provisioning Supabase, Stripe, and your platform.
                            This may take several minutes. This page will
                            refresh automatically when everything is ready.
                          </AlertDescription>
                        </Box>
                        <Spinner size="sm" color="blue.500" ml={2} />
                      </Alert>
                    )}

                  {!hasSubscription && (
                    <Alert
                      status="warning"
                      mt={4}
                      borderRadius="lg"
                      bg="orange.50"
                      borderLeft="4px solid"
                      borderColor="orange.400"
                    >
                      <AlertIcon color="orange.500" />
                      <Box flex="1">
                        <AlertTitle
                          fontSize="md"
                          fontWeight="bold"
                          color="orange.800"
                        >
                          Subscription Required
                        </AlertTitle>
                        <AlertDescription color="orange.700" fontSize="sm">
                          You will need an active subscription to create and
                          keep using your white-labeled AI Voice Agent platform.
                          Subscribe to Get Started!
                        </AlertDescription>
                      </Box>
                      <Button
                        colorScheme="orange"
                        size="sm"
                        onClick={() => navigate("/billing")}
                      >
                        Subscribe Now
                      </Button>
                    </Alert>
                  )}
                </VStack>
              </Container>
            </Box>

            {/* Content */}
            <Container maxW="container.lg" py={8}>
              <Card>
                <CardBody>
                  <VStack spacing={8} align="stretch">
                    {/* Stage Description */}
                    <Box>
                      <Text color="gray.600" lineHeight="tall">
                        {currentStageData.description}
                      </Text>
                    </Box>

                    {/* Stage 1: Branding & AI */}
                    {currentStage === 1 && (
                      <VStack spacing={6} align="stretch">
                        <FormControl>
                          <HStack spacing={2} mb={2}>
                            <FormLabel mb={0}>Application URL</FormLabel>
                            <Tooltip
                              label="The URL where your users will access your white-labeled AI Voice Agent platform (e.g., app.yourdomain.com). Retell AI webhooks will also use this domain."
                              placement="top"
                              hasArrow
                            >
                              <Box>
                                <Icon
                                  as={QuestionIcon}
                                  color="gray.400"
                                  cursor="help"
                                />
                              </Box>
                            </Tooltip>
                          </HStack>
                          <InputGroup>
                            <InputLeftAddon>https://</InputLeftAddon>
                            <Input
                              name="main_app_url"
                              id="main_app_url"
                              autoComplete="url"
                              value={profileData.main_app_url}
                              onChange={(e) =>
                                handleUrlChange("main_app_url", e.target.value)
                              }
                              onBlur={() => handleFieldBlur("main_app_url")}
                              placeholder="app.yourdomain.com"
                            />
                            {verifyingCertificate === "main_app_url" && (
                              <InputRightElement width="auto" pr={2}>
                                <Spinner size="sm" color="blue.500" />
                              </InputRightElement>
                            )}
                            {verifyingCertificate !== "main_app_url" &&
                              profileData.main_app_url_verification_status ===
                                "verified" && (
                                <InputRightElement width="auto" pr={2}>
                                  <HStack spacing={1}>
                                    <CheckIcon color="green.500" boxSize={3} />
                                    <Text
                                      fontSize="xs"
                                      color="green.600"
                                      fontWeight="medium"
                                    >
                                      verified
                                    </Text>
                                  </HStack>
                                </InputRightElement>
                              )}
                            {verifyingCertificate !== "main_app_url" &&
                              profileData.main_app_url_verification_status ===
                                "pending" && (
                                <InputRightElement width="auto" pr={2}>
                                  <Text
                                    fontSize="xs"
                                    color="orange.600"
                                    fontWeight="medium"
                                  >
                                    pending
                                  </Text>
                                </InputRightElement>
                              )}
                          </InputGroup>
                        </FormControl>

                        <FormControl>
                          <HStack spacing={2} mb={2}>
                            <FormLabel mb={0}>Retell AI API Key</FormLabel>
                            <Tooltip
                              label="Your Retell AI API key for voice agent functionality in your white-labeled AI Voice Agent platform."
                              placement="top"
                              hasArrow
                            >
                              <Box>
                                <Icon
                                  as={QuestionIcon}
                                  color="gray.400"
                                  cursor="help"
                                />
                              </Box>
                            </Tooltip>
                          </HStack>
                          <InputGroup>
                            <Input
                              name="retellai_api_key"
                              id="retellai_api_key"
                              type={
                                showKeys.retellai_api_key ? "text" : "password"
                              }
                              autoComplete="new-password"
                              data-lpignore="true"
                              data-1p-ignore
                              value={profileData.retellai_api_key}
                              onChange={(e) =>
                                handleInputChange(
                                  "retellai_api_key",
                                  e.target.value,
                                )
                              }
                              onBlur={() => handleFieldBlur("retellai_api_key")}
                              placeholder="key_..."
                            />
                            <InputRightElement>
                              <IconButton
                                aria-label="Toggle visibility"
                                icon={
                                  showKeys.retellai_api_key ? (
                                    <ViewOffIcon />
                                  ) : (
                                    <ViewIcon />
                                  )
                                }
                                onClick={() =>
                                  toggleShowKey("retellai_api_key")
                                }
                                variant="ghost"
                                size="sm"
                              />
                            </InputRightElement>
                          </InputGroup>
                        </FormControl>

                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
                          <FormControl>
                            <HStack spacing={2} mb={3}>
                              <FormLabel mb={0}>Main Logo</FormLabel>
                              <Tooltip
                                label="Your primary logo displayed throughout the AI Voice Agent platform (recommended: 640x320px, PNG or SVG)"
                                placement="top"
                                hasArrow
                              >
                                <Box>
                                  <Icon
                                    as={QuestionIcon}
                                    color="gray.400"
                                    cursor="help"
                                  />
                                </Box>
                              </Tooltip>
                            </HStack>
                            <Input
                              type="file"
                              accept="image/*"
                              ref={mainLogoInputRef}
                              display="none"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) handleLogoUpload(file, "main");
                              }}
                            />
                            <VStack
                              align="center"
                              justify="center"
                              p={6}
                              borderWidth="2px"
                              borderStyle="dashed"
                              borderColor="gray.300"
                              borderRadius="lg"
                              bg="gray.50"
                              _hover={{
                                bg: "gray.100",
                                borderColor: "blue.400",
                              }}
                              transition="all 0.2s"
                              cursor="pointer"
                              onClick={() => mainLogoInputRef.current?.click()}
                              h="250px"
                              position="relative"
                            >
                              {profileData.main_logo_url ? (
                                <>
                                  <Box
                                    h="150px"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                  >
                                    <Image
                                      src={
                                        profileData.main_logo_url ||
                                        "/placeholder.svg" ||
                                        "/placeholder.svg"
                                      }
                                      alt="Main Logo"
                                      maxH="150px"
                                      maxW="100%"
                                      objectFit="contain"
                                    />
                                  </Box>
                                  <HStack mt={3} spacing={2}>
                                    <Button
                                      size="sm"
                                      colorScheme="blue"
                                      variant="outline"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        mainLogoInputRef.current?.click();
                                      }}
                                    >
                                      Change
                                    </Button>
                                    <IconButton
                                      aria-label="Delete logo"
                                      icon={<FiTrash2 />}
                                      size="sm"
                                      colorScheme="red"
                                      variant="outline"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleLogoDelete("main");
                                      }}
                                    />
                                  </HStack>
                                </>
                              ) : (
                                <>
                                  <Icon
                                    as={FiUpload}
                                    boxSize={8}
                                    color="gray.400"
                                  />
                                  <Text
                                    color="gray.600"
                                    fontSize="sm"
                                    textAlign="center"
                                  >
                                    Click to upload main logo
                                  </Text>
                                  <Text color="gray.500" fontSize="xs">
                                    640x320px recommended
                                  </Text>
                                </>
                              )}
                            </VStack>
                            {uploading.mainLogo && (
                              <Text fontSize="sm" color="blue.600">
                                Uploading...
                              </Text>
                            )}
                          </FormControl>

                          <FormControl>
                            <HStack spacing={2} mb={3}>
                              <FormLabel mb={0}>Tab/Favicon Logo</FormLabel>
                              <Tooltip
                                label="Square icon for browser tab of your white-labeled AI Voice Agent platform (recommended: 32x32px or 64x64px, PNG or ICO)"
                                placement="top"
                                hasArrow
                              >
                                <Box>
                                  <Icon
                                    as={QuestionIcon}
                                    color="gray.400"
                                    cursor="help"
                                  />
                                </Box>
                              </Tooltip>
                            </HStack>
                            <Input
                              type="file"
                              accept="image/*"
                              ref={tabLogoInputRef}
                              display="none"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) handleLogoUpload(file, "tab");
                              }}
                            />
                            <VStack
                              align="center"
                              justify="center"
                              p={6}
                              borderWidth="2px"
                              borderStyle="dashed"
                              borderColor="gray.300"
                              borderRadius="lg"
                              bg="gray.50"
                              _hover={{
                                bg: "gray.100",
                                borderColor: "blue.400",
                              }}
                              transition="all 0.2s"
                              cursor="pointer"
                              onClick={() => tabLogoInputRef.current?.click()}
                              h="250px"
                              position="relative"
                            >
                              {profileData.tab_logo_url ? (
                                <>
                                  <Box
                                    h="150px"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                  >
                                    <Image
                                      src={
                                        profileData.tab_logo_url ||
                                        "/placeholder.svg" ||
                                        "/placeholder.svg"
                                      }
                                      alt="Tab Logo"
                                      maxH="150px"
                                      maxW="100%"
                                      objectFit="contain"
                                    />
                                  </Box>
                                  <HStack mt={3} spacing={2}>
                                    <Button
                                      size="sm"
                                      colorScheme="blue"
                                      variant="outline"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        tabLogoInputRef.current?.click();
                                      }}
                                    >
                                      Change
                                    </Button>
                                    <IconButton
                                      aria-label="Delete logo"
                                      icon={<FiTrash2 />}
                                      size="sm"
                                      colorScheme="red"
                                      variant="outline"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleLogoDelete("tab");
                                      }}
                                    />
                                  </HStack>
                                </>
                              ) : (
                                <>
                                  <Icon
                                    as={FiUpload}
                                    boxSize={8}
                                    color="gray.400"
                                  />
                                  <Text
                                    color="gray.600"
                                    fontSize="sm"
                                    textAlign="center"
                                  >
                                    Click to upload tab/favicon
                                  </Text>
                                  <Text color="gray.500" fontSize="xs">
                                    32x32px or 64x64px recommended
                                  </Text>
                                </>
                              )}
                            </VStack>
                            {uploading.tabLogo && (
                              <Text fontSize="sm" color="blue.600">
                                Uploading...
                              </Text>
                            )}
                          </FormControl>
                        </SimpleGrid>
                      </VStack>
                    )}

                    {/* Stage 2: Billing & Payments */}
                    {currentStage === 2 && (
                      <VStack spacing={6} align="stretch">
                        <FormControl>
                          <HStack spacing={2} mb={2}>
                            <FormLabel mb={0}>Stripe Secret Key</FormLabel>
                            <Tooltip
                              label="Your Stripe secret key for processing subscriptions and credit purchases in your white-labeled AI Voice Agent platform."
                              placement="top"
                              hasArrow
                            >
                              <Box>
                                <Icon
                                  as={QuestionIcon}
                                  color="gray.400"
                                  cursor="help"
                                />
                              </Box>
                            </Tooltip>
                          </HStack>
                          <InputGroup>
                            <Input
                              name="stripe_secret_key"
                              id="stripe_secret_key"
                              type={
                                showKeys.stripe_secret_key ? "text" : "password"
                              }
                              autoComplete="new-password"
                              data-lpignore="true"
                              data-1p-ignore
                              value={profileData.stripe_secret_key}
                              onChange={(e) =>
                                handleInputChange(
                                  "stripe_secret_key",
                                  e.target.value,
                                )
                              }
                              onBlur={() =>
                                handleFieldBlur("stripe_secret_key")
                              }
                              placeholder="sk_..."
                            />
                            <InputRightElement>
                              <IconButton
                                aria-label="Toggle visibility"
                                icon={
                                  showKeys.stripe_secret_key ? (
                                    <ViewOffIcon />
                                  ) : (
                                    <ViewIcon />
                                  )
                                }
                                onClick={() =>
                                  toggleShowKey("stripe_secret_key")
                                }
                                variant="ghost"
                                size="sm"
                              />
                            </InputRightElement>
                          </InputGroup>
                        </FormControl>

                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                          <FormControl>
                            <HStack spacing={2} mb={2}>
                              <FormLabel mb={0}>
                                Monthly Subscription Price (in USD)
                              </FormLabel>
                              <Tooltip
                                label="The monthly subscription price your end users will pay to access your white-labeled AI Voice Agent platform."
                                placement="top"
                                hasArrow
                              >
                                <Box>
                                  <Icon
                                    as={QuestionIcon}
                                    color="gray.400"
                                    cursor="help"
                                  />
                                </Box>
                              </Tooltip>
                            </HStack>
                            <PriceNumberInput
                              min={0}
                              step={1}
                              precision={0}
                              placeholder="0"
                              value={profileData.monthly_subscription_price_usd}
                              onChange={(valueAsString, valueAsNumber) =>
                                handleNumberChange(
                                  "monthly_subscription_price_usd",
                                  valueAsString,
                                  valueAsNumber,
                                  true,
                                )
                              }
                              onBlur={() =>
                                handleFieldBlur(
                                  "monthly_subscription_price_usd",
                                )
                              }
                            />
                          </FormControl>

                          <FormControl>
                            <HStack spacing={2} mb={2}>
                              <FormLabel mb={0}>
                                One Credit Price (in USD)
                              </FormLabel>
                              <Tooltip
                                label="The price per credit that your end users pay when purchasing additional credits beyond their subscription allowance."
                                placement="top"
                                hasArrow
                              >
                                <Box>
                                  <Icon
                                    as={QuestionIcon}
                                    color="gray.400"
                                    cursor="help"
                                  />
                                </Box>
                              </Tooltip>
                            </HStack>
                            <PriceNumberInput
                              min={0}
                              step={0.01}
                              precision={2}
                              placeholder="0.00"
                              value={profileData.one_credit_price_usd}
                              onChange={(valueAsString, valueAsNumber) =>
                                handleNumberChange(
                                  "one_credit_price_usd",
                                  valueAsString,
                                  valueAsNumber,
                                )
                              }
                              onBlur={() =>
                                handleFieldBlur("one_credit_price_usd")
                              }
                            />
                          </FormControl>

                          <FormControl>
                            <HStack spacing={2} mb={2}>
                              <FormLabel mb={0}>
                                One Phone Number Monthly Price (in USD)
                              </FormLabel>
                              <Tooltip
                                label="The monthly price you charge end users per phone number. Note: RetellAI will charge you $2/month for each phone number on your account."
                                placement="top"
                                hasArrow
                              >
                                <Box>
                                  <Icon
                                    as={QuestionIcon}
                                    color="gray.400"
                                    cursor="help"
                                  />
                                </Box>
                              </Tooltip>
                            </HStack>
                            <PriceNumberInput
                              min={0}
                              step={0.01}
                              precision={2}
                              placeholder="0.00"
                              value={
                                profileData.one_phone_number_monthly_price_usd
                              }
                              onChange={(valueAsString, valueAsNumber) =>
                                handleNumberChange(
                                  "one_phone_number_monthly_price_usd",
                                  valueAsString,
                                  valueAsNumber,
                                )
                              }
                              onBlur={() =>
                                handleFieldBlur(
                                  "one_phone_number_monthly_price_usd",
                                )
                              }
                            />
                          </FormControl>

                          <FormControl>
                            <HStack spacing={2} mb={2}>
                              <FormLabel mb={0}>
                                AI Agents Call Price Per Minute (in USD)
                              </FormLabel>
                              <Tooltip
                                label="The per-minute call price you charge end users for AI agent calls. Note: RetellAI per-minute call costs typically range from $0.07 to $0.31 depending on agent configuration."
                                placement="top"
                                hasArrow
                              >
                                <Box>
                                  <Icon
                                    as={QuestionIcon}
                                    color="gray.400"
                                    cursor="help"
                                  />
                                </Box>
                              </Tooltip>
                            </HStack>
                            <PriceNumberInput
                              min={0}
                              step={0.01}
                              precision={2}
                              placeholder="0.00"
                              value={
                                profileData.ai_agents_call_price_per_minute_usd
                              }
                              onChange={(valueAsString, valueAsNumber) =>
                                handleNumberChange(
                                  "ai_agents_call_price_per_minute_usd",
                                  valueAsString,
                                  valueAsNumber,
                                )
                              }
                              onBlur={() =>
                                handleFieldBlur(
                                  "ai_agents_call_price_per_minute_usd",
                                )
                              }
                            />
                          </FormControl>
                        </SimpleGrid>
                      </VStack>
                    )}

                    {/* Navigation Buttons */}
                    <Flex justify="space-between" pt={6}>
                      <Button
                        variant="ghost"
                        size="lg"
                        onClick={() =>
                          setCurrentStage((prev) => Math.max(1, prev - 1))
                        }
                        isDisabled={currentStage === 1}
                      >
                        Previous
                      </Button>

                      {(() => {
                        const isCompleteStep = currentStage === 2;

                        // ✅ Only disable on the final step if subscription, stages, or instance setup is in progress
                        const isInstanceSetupInProgress =
                          deploymentInProgress ||
                          !!profileData.setup_completed ||
                          isInstanceProvisioning(profileData);

                        const isButtonDisabled = isCompleteStep
                          ? !hasSubscription ||
                            !allStagesComplete ||
                            isInstanceSetupInProgress
                          : false; // Next is always clickable on stages 1 & 2

                        // Decide tooltip label
                        let tooltipLabel = "";

                        if (isCompleteStep) {
                          if (isInstanceSetupInProgress) {
                            tooltipLabel =
                              "Instance creation is in progress. This typically takes 10-15 minutes. Please check back shortly.";
                          } else if (!hasSubscription) {
                            tooltipLabel =
                              "Please subscribe to a plan in the Billing section to complete setting up your white-labeled AI Voice Agent platform";
                          } else if (!allStagesComplete) {
                            tooltipLabel =
                              "Please complete all required fields in all stages before initiating instance creation";
                          }
                        }

                        return (
                          <Tooltip
                            label={tooltipLabel}
                            isDisabled={!tooltipLabel}
                            placement="top"
                            hasArrow
                          >
                            <Button
                              variant={isCompleteStep ? "solid" : "ghost"}
                              size="lg"
                              onClick={handleSaveAndContinue}
                              isLoading={loading}
                              loadingText="......"
                              isDisabled={isButtonDisabled}
                            >
                              {isCompleteStep
                                ? "Initiate Instance Creation"
                                : "Next"}
                            </Button>
                          </Tooltip>
                        );
                      })()}
                    </Flex>
                  </VStack>
                </CardBody>
              </Card>
            </Container>
          </>
        )}

        <DnsRecordsDialog
          isOpen={dnsDialogOpen}
          onClose={() => setDnsDialogOpen(false)}
          dnsRecords={dnsRecords}
          domain={dnsDialogDomain}
        />

        <DnsRecordsDialog
          isOpen={showCnameDialog}
          onClose={() => setShowCnameDialog(false)}
          dnsRecords={cnameRecords}
          domain={profileData.main_app_url || ""}
          onVerify={handleCnameConfirmed}
          isVerifying={isConfirmingCname}
        />

        <RetellWebhookDialog
          isOpen={showRetellWebhookDialog}
          onClose={() => setShowRetellWebhookDialog(false)}
          applicationUrl={profileData.main_app_url || ""}
          onComplete={handleRetellWebhookComplete}
          isLoading={isCompletingRetellSetup}
        />
      </Container>
    </Box>
  );
};

export default OnboardingJourney;
